/**
 * Campaign Results visual theme — aligned with the Beatly Design System tokens
 * defined in src/styles/globals.css (--beatly-*, --chart-1..5).
 *
 * Primary accent:  electric purple (Beatly brand)
 * Positive signal: teal (Beatly success)
 * Chart palette:   --chart-1..5 (purple, teal, amber, blue, warning-red)
 *
 * Recharts / inline SVG attributes don't resolve `var(--x)` inside stop-color
 * and fill gradients in older WebKit, so we keep resolved hex values next to
 * the CSS-variable references. The hex must mirror globals.css tokens — update
 * both if a token changes.
 */

/**
 * Beatly brand palette (per the brand guideline). Use these hex values for
 * charts and accents; CSS-variable equivalents come from globals.css when
 * shipped via the design system.
 *
 *   Electric purple   #be43ff   primary brand accent
 *   Brand purple      #2e024c   deep — used for dark text on accent tints
 *   Neon green        #a9f600   secondary accent / "active" highlight
 *   Teal              #00b1a5   third accent
 *   Light pink        #ff9cff   tint
 *   Orange/red        #ff5635   warning + variation
 *   Yellow            #f7f800   warm tint
 *   Forest green      #00975d   positive + variation
 *   Bright green      #00b100   positive variation
 */
export const THEME = {
  // Primary accent (electric purple)
  accent: 'var(--beatly-electric-purple)',
  accentHex: '#be43ff',
  accentSoft: '#F4E6FF', // ~12% tint of electric purple; no token exists
  accentBorder: '#DCC2FF',
  accentText: 'var(--beatly-brand-purple)', // deep purple for text on accent tint

  // Positive / earned indicator (teal)
  positive: 'var(--beatly-teal)',
  positiveHex: '#00b1a5',

  // Neon green — secondary accent (headline highlight, EMV value, "active" pill)
  neon: 'var(--beatly-neon-green)',
  neonHex: '#a9f600',
  neonInk: '#1c3a00', // legible dark text on neon-green fills

  // Brand purple (primary button bg, dark headings)
  brand: 'var(--beatly-brand-purple)',
  brandHex: '#2e024c',

  // Additional brand colors — use for chart slices and section accents.
  pinkHex: '#ff9cff',
  orangeHex: '#ff5635',
  yellowHex: '#f7f800',
  forestHex: '#00975d',
  brightGreenHex: '#00b100',

  // Chart palette (mirrors --chart-1..5 in globals.css; legacy aliases
  // — prefer CHART_PALETTE / paletteColor() for new code).
  chart1: 'var(--chart-1)',
  chart1Hex: '#be43ff',
  chart2: 'var(--chart-2)',
  chart2Hex: '#a9f600',
  chart3: 'var(--chart-3)',
  chart3Hex: '#00b1a5',
  chart4: 'var(--chart-4)',
  chart4Hex: '#ff5635',
  chart5: 'var(--chart-5)',
  chart5Hex: '#ff9cff',

  // Utility
  grid: '#EAECF0',
  border: '#EAECF0',
  text: 'var(--foreground)',
  textMuted: 'var(--muted-foreground)',

  // Background for the sentiment placeholder card — Beatly brand gradient
  sentimentGradient:
    'linear-gradient(135deg, rgba(190,67,255,0.25) 0%, rgba(169,246,0,0.25) 50%, rgba(0,177,165,0.25) 100%)',
};

/** Tier color-coding follows the brand palette so it reads as a progression. */
export const TIER_COLORS: Record<string, string> = {
  nano: THEME.accentHex,    // electric purple
  micro: THEME.neonHex,     // neon green
  macro: THEME.positiveHex, // teal
  mega: THEME.orangeHex,    // orange/red
  unknown: '#CBD5E1',
};

/**
 * Canonical chart palette for Results panels — sourced from the Beatly brand
 * guideline (electric purple, neon green, teal, orange, light pink, yellow,
 * deep purple, forest green). Neon green sits in slot 2 so secondary series
 * picks up the brand's signature accent. Use `paletteColor(idx)` to assign
 * stable colors to series in any order.
 */
export const CHART_PALETTE: readonly string[] = [
  THEME.accentHex,      // #be43ff electric purple — primary
  THEME.neonHex,        // #a9f600 neon green — secondary (per brand)
  THEME.positiveHex,    // #00b1a5 teal
  THEME.orangeHex,      // #ff5635 orange/red
  THEME.pinkHex,        // #ff9cff light pink
  THEME.yellowHex,      // #f7f800 yellow
  THEME.forestHex,      // #00975d forest green
  THEME.brandHex,       // #2e024c deep purple
];

/** Stable color for a series at index i — wraps around if i >= palette length. */
export function paletteColor(i: number): string {
  return CHART_PALETTE[((i % CHART_PALETTE.length) + CHART_PALETTE.length) % CHART_PALETTE.length];
}
